import SearchIcon from '@mui/icons-material/Search'
import { SelectChangeEvent } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import Pagination from '@mui/material/Pagination'
import { getEmailMarketingCampaigns } from 'api/digitalMarketing'
import { EmailMarketingCampaign } from 'api/models'
import EmailMarketingDetailsDrawer from 'components/EmailMarketingDetailsDrawer/EmailMarketingDetailsDrawer'
import LoadingSkeleton from 'components/LoadingSkeleton/LoadingSkeleton'
import useSafeAsync from 'hooks/useSafeAsync'
import { debounce, deburr, uniqBy } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { Location } from 'types'

import { CampaignItem, NoData, Skeleton } from './components'
import {
  Container,
  CurrentPageInfo,
  EmailMarketingCampaignsTable,
  PaginationSelect,
  PaginationText,
  PaginationWrapper,
  RowsPerPageWrapper,
  SearchIconWrapper,
  SearchInput,
  Tab,
  TableHeader,
  Tabs,
  TabsContainer,
  EmptyStateWrapper,
  EmptyStateText
} from './styles'


interface EmailMarketingCampaignsProps {
  partners: Location[]
}

const EmailMarketingCampaigns = ({ partners }: EmailMarketingCampaignsProps) => {
  const [isSearching, setIsSearching] = useState(false)
  const [emailMarketingCampaigns, setEmailMarketingCampaigns] = useState<EmailMarketingCampaign[]>(
    [],
  )
  const [currentStatusFilter, setCurrentStatusFilter] = useState('all')
  const [searchVisible, setSearchVisible] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [page, setPage] = useState(1)
  const [selectedItem, setSelectedItem] = useState<EmailMarketingCampaign | null>(null)
  const filterByStatus = (element: EmailMarketingCampaign, statusFiltered?: string) => {
    const filter = statusFiltered || currentStatusFilter
    if (filter === 'all') return element

    // Status mapping
    const statusMappings: Record<string, string[]> = {
      draft: ['draft'],
      pending_approval: ['pending_internal_approval', 'pending_approval'],
      approved: ['internally_approved', 'approved', 'scheduled'],
      sent: ['sending', 'sent'],
      rejected: ['rejected', 'error'],
    }

    if (statusMappings[filter] && statusMappings[filter].includes(element.status)) {
      return element
    }

    return false
  }

  const hits = emailMarketingCampaigns?.filter((i) => filterByStatus(i, currentStatusFilter))

  const toggleSearch = () => {
    setSearchVisible(!searchVisible)
  }

  const handleSearchChange = async (val: string) => {
    if (!partners?.length) return
    setIsSearching(true)

    const uniqueOrgPartners = uniqBy(
      partners.filter((partner) => partner?.organization_id),
      'organization_id',
    )

    const campaignPromises = uniqueOrgPartners.map((partner) =>
      getEmailMarketingCampaigns(partner.organization_id || '').catch((e) => {
        console.error(`Error fetching campaigns for org ${partner.organization_id}`, e)
        return []
      }),
    )

    const campaignResults = await Promise.all(campaignPromises)
    const allCampaigns = campaignResults.flat()

    const filteredCampaigns = val
      ? allCampaigns.filter((campaign) => campaign.name.toLowerCase().includes(val.toLowerCase()))
      : allCampaigns

    setEmailMarketingCampaigns(filteredCampaigns)
    setIsSearching(false)
  }

  const safeAsync = useSafeAsync()

  const fetchCampaigns = useCallback(async () => {
    if (!partners?.length) return
    setIsSearching(true)

    const uniqueOrgPartners = uniqBy(
      partners.filter((partner) => partner?.organization_id),
      'organization_id',
    )

    const campaignPromises = uniqueOrgPartners.map((partner) =>
      getEmailMarketingCampaigns(partner.organization_id || '')
        .then((campaigns) =>
          campaigns.map((campaign) => ({
            ...campaign,
            organizationName: partner.organization_name,
            organizationId: partner.organization_id,
          })),
        )
        .catch((e) => {
          console.error(`Error fetching campaigns for org ${partner.organization_id}`, e)
          return []
        }),
    )

    const campaignResults = await Promise.all(campaignPromises)
    const allCampaigns = campaignResults.flat()

    setEmailMarketingCampaigns(allCampaigns)
    setIsSearching(false)
  }, [partners])

  const debouncedFetch = debounce(handleSearchChange, 500)

  const isActive = (scope: string) => {
    if (currentStatusFilter === scope) return true
    return false
  }

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: SelectChangeEvent<unknown>) => {
    setRowsPerPage(Number(event.target.value))
    setPage(1)
  }

  const startRow = (page - 1) * rowsPerPage
  const endRow = Math.min(startRow + rowsPerPage, hits.length)
  const paginatedHits = hits?.slice(startRow, endRow)

  useEffect(() => {
    safeAsync(fetchCampaigns()).catch((e) => {
      console.error(e)
      setIsSearching(false)
    })
  }, [partners, safeAsync, fetchCampaigns])

  const handleOnShowItem = useCallback((item: EmailMarketingCampaign) => {
    setSelectedItem(item)
    // const baseUrl = emailMarketingApi.hostUrl;
    // let url = `${baseUrl}/campaigns/${item.id}`;

    // const editStatuses = ["draft", "pending_approval"];

    // if (editStatuses.includes(item.status)) {
    //   url += `/edit`;
    // }

    // window.open(url, '_blank')
  }, [])

  return (
    isSearching ? (
      <LoadingSkeleton lineCount={3} />
    ) : (
      <EmptyStateWrapper>
      {hits.length > 0 ? (
        <Container>
          <TabsContainer>
            <Tabs>
              <Tab active={isActive('all')} onClick={() => setCurrentStatusFilter('all')}>
                All ({emailMarketingCampaigns?.filter((i) => filterByStatus(i, 'all')).length})
              </Tab>
              <Tab active={isActive('draft')} onClick={() => setCurrentStatusFilter('draft')}>
                Draft ({emailMarketingCampaigns?.filter((i) => filterByStatus(i, 'draft')).length})
              </Tab>
              <Tab
                active={isActive('pending_approval')}
                onClick={() => setCurrentStatusFilter('pending_approval')}
              >
                Pending Approval (
                {emailMarketingCampaigns?.filter((i) => filterByStatus(i, 'pending_approval')).length})
              </Tab>
              <Tab active={isActive('approved')} onClick={() => setCurrentStatusFilter('approved')}>
                Approved ({emailMarketingCampaigns?.filter((i) => filterByStatus(i, 'approved')).length}
                )
              </Tab>
              <Tab active={isActive('rejected')} onClick={() => setCurrentStatusFilter('rejected')}>
                Rejected ({emailMarketingCampaigns?.filter((i) => filterByStatus(i, 'rejected')).length}
                )
              </Tab>
              <Tab active={isActive('sent')} onClick={() => setCurrentStatusFilter('sent')}>
                Sent ({emailMarketingCampaigns?.filter((i) => filterByStatus(i, 'sent')).length})
              </Tab>
            </Tabs>
            <div>
              {searchVisible ? (
                <SearchInput
                  visible={searchVisible}
                  type='text'
                  placeholder='Search by name...'
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value)
                    const trimmedValue = deburr(e.target.value.trim()).toLowerCase()
                    debouncedFetch(trimmedValue)
                  }}
                  autoFocus
                />
              ) : (
                <SearchIconWrapper onClick={toggleSearch} data-testid='search-icon'>
                  <SearchIcon />
                </SearchIconWrapper>
              )}
            </div>
          </TabsContainer>
          <EmailMarketingCampaignsTable>
            <TableHeader>
              <div>Campaign Name</div>
              <div>Subject</div>
              <div>Send Date</div>
              <div>Status</div>
              <div>Delivered Messages</div>
              <div>Opened Messages</div>
              <div>Clicked Messages</div>
            </TableHeader>
            <div data-cy='table-body'>
              {isSearching ? (
                <Skeleton />
              ) : !partners.length || hits.length === 0 ? (
                <NoData />
              ) : (
                paginatedHits?.map((campaign) => {
                  return <CampaignItem key={campaign.id} item={campaign} onAction={handleOnShowItem} />
                })
              )}
            </div>
            {hits?.length > 0 && (
              <PaginationWrapper>
                <RowsPerPageWrapper>
                  <PaginationText>Rows per Page:</PaginationText>
                  <PaginationSelect value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </PaginationSelect>
                </RowsPerPageWrapper>

                <CurrentPageInfo>
                  {startRow + 1}-{endRow} of {hits?.length}
                </CurrentPageInfo>

                <Pagination
                  count={Math.ceil(hits?.length / rowsPerPage)}
                  page={page}
                  onChange={handleChangePage}
                  variant='text'
                  shape='rounded'
                />
              </PaginationWrapper>
            ) }
          </EmailMarketingCampaignsTable>
          {selectedItem && partners.length > 0 && (
            <EmailMarketingDetailsDrawer
              open
              item={selectedItem}
              onClose={() => setSelectedItem(null)}
            />
          )}
        </Container>
       ) : (
        <EmptyStateText>
          No Campaigns Available
        </EmptyStateText>
      )}
      </EmptyStateWrapper>
    ) 
  )
}

export default EmailMarketingCampaigns
